import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Button, Card, CardContent, Chip, Snackbar, Stack, Switch, Typography,
} from '@mui/material';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';
import StateBoundary from '../components/StateBoundary';
import TriggerDialog from '../components/TriggerDialog';
import { useSession } from '../viewmodels/SessionViewModel';
import { useTriggersViewModel } from '../viewmodels/TriggersViewModel';
import { formatDateTime } from '../utils/format';

const STATUS_TEXT = {
  baseline_set: 'Ready: watching for anything new from now on',
  no_new: 'Nothing new',
  fired: 'Sent to the team',
  cooling_down: 'Waiting: the minimum wait between messages has not passed',
  waiting_busy: 'Waiting: the team was busy, trying again shortly',
  daily_limit: 'Paused: daily limit for automatic runs reached',
  not_connected: 'The app is not connected',
  chat_missing: 'Switched off: its chat no longer exists',
  error: 'Could not check the app',
};

function TriggerCard({
  t, isOwner, busy, onToggle, onCheck, onEdit, onDelete,
}) {
  const problem = ['error', 'not_connected', 'chat_missing'].includes(t.last_status);
  return (
    <Card sx={{ opacity: t.enabled ? 1 : 0.75 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <div>
            <Typography variant="h6">{t.title}</Typography>
            <Typography color="text.secondary">
              {`When: ${t.event_label.charAt(0).toLowerCase()}${t.event_label.slice(1)}${t.event === 'low_stock' ? ` (${t.threshold} or fewer)` : ''}`}
            </Typography>
          </div>
          {isOwner && <Switch checked={t.enabled} onChange={onToggle} disabled={busy} inputProps={{ 'aria-label': `Switch ${t.title} on or off` }} />}
        </Stack>
        <Typography sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>{t.prompt}</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={`Sent to ${t.conversation.title}`} />
          <Chip size="small" variant="outlined" label={`At most every ${t.cooldown_minutes} min`} />
          {!t.enabled && <Chip size="small" label="Off" />}
          {!t.connected && <Chip size="small" color="warning" label="App not connected" />}
          {t.last_status && (
            <Chip size="small" color={problem ? 'error' : 'default'} variant="outlined" label={STATUS_TEXT[t.last_status] || t.last_status} />
          )}
          {t.last_fired_at && <Chip size="small" variant="outlined" label={`Last sent: ${formatDateTime(t.last_fired_at)}`} />}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {isOwner && <Button size="small" variant="outlined" disabled={busy} onClick={onCheck}>Check now</Button>}
          {isOwner && <Button size="small" disabled={busy} onClick={onEdit}>Edit</Button>}
          {isOwner && <Button size="small" color="error" disabled={busy} onClick={onDelete}>Delete</Button>}
          {t.last_run_id && <Button size="small" component={RouterLink} to={`/chat/${t.conversation.id}`}>Open chat</Button>}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function TriggersView() {
  const { isOwner } = useSession();
  const vm = useTriggersViewModel();
  const [dialog, setDialog] = useState({ open: false, trigger: null });
  const [confirm, setConfirm] = useState(null);

  const openNew = () => { vm.clearSaveError(); setDialog({ open: true, trigger: null }); };
  const save = async (fields) => {
    const ok = dialog.trigger ? await vm.update(dialog.trigger.id, fields) : await vm.create(fields);
    if (ok) setDialog({ open: false, trigger: null });
  };

  return (
    <>
      <PageHeader
        title="Triggers"
        subtitle="Wake your team when something happens: a new lead, a new order or low stock. The apps are checked every 10 minutes without using your AI key."
        actions={isOwner && <Button variant="contained" startIcon={<BoltOutlinedIcon />} onClick={openNew}>New trigger</Button>}
      />
      <StateBoundary
        loading={vm.loading}
        error={vm.error}
        onRetry={vm.reload}
        isEmpty={vm.triggers.length === 0}
        empty={{
          icon: <BoltOutlinedIcon fontSize="large" />,
          title: 'No triggers yet',
          body: 'A trigger watches a connected app and messages your team only when something new appears. The team then answers in the chat using your AI key, so runs are limited per day.',
          action: isOwner ? <Button variant="contained" onClick={openNew}>New trigger</Button> : undefined,
        }}
      >
        <Stack spacing={2}>
          {vm.triggers.map((t) => (
            <TriggerCard
              key={t.id}
              t={t}
              isOwner={isOwner}
              busy={vm.saving}
              onToggle={() => vm.toggle(t)}
              onCheck={() => vm.checkNow(t.id)}
              onEdit={() => { vm.clearSaveError(); setDialog({ open: true, trigger: t }); }}
              onDelete={() => setConfirm(t)}
            />
          ))}
        </Stack>
      </StateBoundary>
      <TriggerDialog
        open={dialog.open}
        trigger={dialog.trigger}
        chats={vm.chats}
        saving={vm.saving}
        error={vm.saveError}
        onClose={() => setDialog({ open: false, trigger: null })}
        onSave={save}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title="Delete this trigger?"
        body={`"${confirm?.title}" will stop watching. Messages it already sent stay in the chat.`}
        confirmLabel="Delete"
        color="error"
        busy={vm.saving}
        onConfirm={async () => { await vm.remove(confirm.id); setConfirm(null); }}
        onClose={() => setConfirm(null)}
      />
      <Snackbar open={Boolean(vm.feedback)} autoHideDuration={6000} onClose={vm.clearFeedback} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {vm.feedback ? <Alert severity={vm.feedback.severity} onClose={vm.clearFeedback}>{vm.feedback.text}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
